import { z } from "zod";

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const nullableIsoDateSchema = isoDateSchema.nullable();
const nullableString = (maxLength: number) => z.string().trim().min(1).max(maxLength).nullable();
const sortOrderSchema = z.number().int().min(0);
const languageCodeSchema = z.string().regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/).max(16);

export const specialistLanguageProficiencies = ["a1", "a2", "b1", "b2", "c1", "c2", "native"] as const;
export const specialistLanguageProficiencySchema = z.enum(specialistLanguageProficiencies);

export const specialistEducationSchema = z.object({
  id: z.string().uuid(),
  specialistProfileId: z.string().uuid(),
  institutionName: z.string(),
  degree: z.string().nullable(),
  fieldOfStudy: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  isCurrent: z.boolean(),
  description: z.string().nullable(),
  sortOrder: z.number().int(),
  createdByUserId: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const specialistCertificateSchema = z.object({
  id: z.string().uuid(),
  specialistProfileId: z.string().uuid(),
  certificateName: z.string(),
  issuerName: z.string(),
  issuedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  credentialId: z.string().nullable(),
  credentialUrl: z.string().nullable(),
  description: z.string().nullable(),
  sortOrder: z.number().int(),
  createdByUserId: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const specialistLanguageSchema = z.object({
  id: z.string().uuid(),
  specialistProfileId: z.string().uuid(),
  languageCode: z.string(),
  languageName: z.string(),
  proficiency: specialistLanguageProficiencySchema,
  isPrimary: z.boolean(),
  certificateName: z.string().nullable(),
  notes: z.string().nullable(),
  sortOrder: z.number().int(),
  createdByUserId: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const specialistEducationCreateRequestSchema = z
  .object({
    institutionName: z.string().trim().min(1).max(180),
    degree: nullableString(160).optional(),
    fieldOfStudy: nullableString(160).optional(),
    startDate: nullableIsoDateSchema.optional(),
    endDate: nullableIsoDateSchema.optional(),
    isCurrent: z.boolean().default(false),
    description: nullableString(4_000).optional(),
    sortOrder: sortOrderSchema.default(0),
  })
  .strict();

export const specialistEducationUpdateRequestSchema = z
  .object({
    institutionName: z.string().trim().min(1).max(180).optional(),
    degree: nullableString(160).optional(),
    fieldOfStudy: nullableString(160).optional(),
    startDate: nullableIsoDateSchema.optional(),
    endDate: nullableIsoDateSchema.optional(),
    isCurrent: z.boolean().optional(),
    description: nullableString(4_000).optional(),
    sortOrder: sortOrderSchema.optional(),
  })
  .strict();

export const specialistCertificateCreateRequestSchema = z
  .object({
    certificateName: z.string().trim().min(1).max(180),
    issuerName: z.string().trim().min(1).max(180),
    issuedAt: nullableIsoDateSchema.optional(),
    expiresAt: nullableIsoDateSchema.optional(),
    credentialId: nullableString(180).optional(),
    credentialUrl: nullableString(300).optional(),
    description: nullableString(4_000).optional(),
    sortOrder: sortOrderSchema.default(0),
  })
  .strict();

export const specialistCertificateUpdateRequestSchema = z
  .object({
    certificateName: z.string().trim().min(1).max(180).optional(),
    issuerName: z.string().trim().min(1).max(180).optional(),
    issuedAt: nullableIsoDateSchema.optional(),
    expiresAt: nullableIsoDateSchema.optional(),
    credentialId: nullableString(180).optional(),
    credentialUrl: nullableString(300).optional(),
    description: nullableString(4_000).optional(),
    sortOrder: sortOrderSchema.optional(),
  })
  .strict();

export const specialistLanguageCreateRequestSchema = z
  .object({
    languageCode: languageCodeSchema,
    languageName: z.string().trim().min(1).max(120),
    proficiency: specialistLanguageProficiencySchema,
    isPrimary: z.boolean().default(false),
    certificateName: nullableString(180).optional(),
    notes: nullableString(4_000).optional(),
    sortOrder: sortOrderSchema.default(0),
  })
  .strict();

export const specialistLanguageUpdateRequestSchema = z
  .object({
    languageCode: languageCodeSchema.optional(),
    languageName: z.string().trim().min(1).max(120).optional(),
    proficiency: specialistLanguageProficiencySchema.optional(),
    isPrimary: z.boolean().optional(),
    certificateName: nullableString(180).optional(),
    notes: nullableString(4_000).optional(),
    sortOrder: sortOrderSchema.optional(),
  })
  .strict();

export const specialistEducationListResponseSchema = z.object({
  items: z.array(specialistEducationSchema),
  total: z.number().int().nonnegative(),
});

export const specialistCertificateListResponseSchema = z.object({
  items: z.array(specialistCertificateSchema),
  total: z.number().int().nonnegative(),
});

export const specialistLanguageListResponseSchema = z.object({
  items: z.array(specialistLanguageSchema),
  total: z.number().int().nonnegative(),
});

export type SpecialistLanguageProficiency = z.infer<typeof specialistLanguageProficiencySchema>;
export type SpecialistEducation = z.infer<typeof specialistEducationSchema>;
export type SpecialistCertificate = z.infer<typeof specialistCertificateSchema>;
export type SpecialistLanguage = z.infer<typeof specialistLanguageSchema>;
export type SpecialistEducationCreateRequest = z.infer<typeof specialistEducationCreateRequestSchema>;
export type SpecialistEducationUpdateRequest = z.infer<typeof specialistEducationUpdateRequestSchema>;
export type SpecialistCertificateCreateRequest = z.infer<typeof specialistCertificateCreateRequestSchema>;
export type SpecialistCertificateUpdateRequest = z.infer<typeof specialistCertificateUpdateRequestSchema>;
export type SpecialistLanguageCreateRequest = z.infer<typeof specialistLanguageCreateRequestSchema>;
export type SpecialistLanguageUpdateRequest = z.infer<typeof specialistLanguageUpdateRequestSchema>;
export type SpecialistEducationListResponse = z.infer<typeof specialistEducationListResponseSchema>;
export type SpecialistCertificateListResponse = z.infer<typeof specialistCertificateListResponseSchema>;
export type SpecialistLanguageListResponse = z.infer<typeof specialistLanguageListResponseSchema>;
